import React from 'react';
import { useTranslation } from 'react-i18next';
import { ensureNamespaces } from '../i18n';

interface NamespacesProps {
  namespaces: readonly string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Holds back `children` until every namespace it needs is in i18next.
 *
 * Without this, a route reached by client-side navigation would render one
 * frame with raw keys (`audit.title`) before its dictionary file arrived:
 * i18next returns the key itself for an unknown namespace, and React commits
 * that frame. Blocking here is safe because the page's own chunk is loading
 * over the same connection anyway, and the reserved space is already in place.
 */
export function Namespaces({ namespaces, children, fallback = null }: NamespacesProps) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  // A new array identity on every render would refetch forever; the generated
  // map returns a stable reference, so the join is the real cache key.
  const key = namespaces.join('|');

  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!key) {
      setReady(true);
      return;
    }
    void ensureNamespaces(lang, key.split('|')).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [key, lang]);

  if (!ready) return <>{fallback}</>;
  return <>{children}</>;
}
